import { skeleton } from './loadingState.js';
/**
 * statCard.js — the one KPI/summary card.
 *
 * There were three copies. The dashboard's had an `href` option that turned the card into
 * a link to the page the number comes from; analytics and ad-performance had a cut-down
 * positional clone with no `href` at all, so those pages could not offer the click-through
 * even though the CSS for it (`.stat-card--link`, `.stat-go`) was already global and
 * already shipped. A fourth copy was built imperatively in ad-performance by assigning
 * `.stat-card` innerHTML by hand. Four renderings of one component is how "make the
 * summary boxes clickable" became a per-page request instead of a single change.
 *
 * `href` stays OPT-IN per card. A KPI that clicks through to a page which cannot show you
 * the rows behind the number is a dead end, not a fix — it satisfies the click and answers
 * nothing. So a card gets a link only when a real destination lists what it counts.
 *
 * The affordance is deliberate too: linked cards render a `north_east` glyph, revealed on
 * hover and pinned on for touch (dashboard-kit.css). Without it a clickable card looks
 * exactly like a static one.
 */

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

/**
 * @param {object} opts
 * @param {string} opts.icon - Material Symbols glyph name
 * @param {string} opts.label - what the number is
 * @param {string} [opts.valueId] - id the loader writes the value into
 * @param {string} [opts.value] - initial value; '—' until data lands
 * @param {boolean} [opts.loading=false] - show a placeholder until the value loader completes
 * @param {string} [opts.subId] - id for the sub-label
 * @param {string} [opts.sub] - sub-label text
 * @param {string} [opts.href] - destination; omit when no page can answer the click
 * @param {string} [opts.trendId] - id for an optional trend chip
 * @returns {string} HTML
 */
export function statCard({ icon, label, valueId, value = '—', loading = false, subId, sub = '', href, trendId } = {}) {
  const tag = href ? 'a' : 'div';
  const attrs = href
    ? ` href="${esc(href)}" class="stat-card stat-card--link"`
    : ' class="stat-card"';
  return `
  <${tag}${attrs}>
    <div class="stat-header">
      <span class="material-symbols-outlined stat-icon" aria-hidden="true">${esc(icon)}</span>
      <span class="stat-label">${esc(label)}</span>
      ${href ? '<span class="stat-go material-symbols-outlined" aria-hidden="true">north_east</span>' : ''}
    </div>
    <div class="stat-value"${valueId ? ` id="${esc(valueId)}"` : ''}>${loading ? `<span class="app-loading-value" data-loading-state>${skeleton('value')}<span class="app-loading-sr">Loading ${esc(label)}</span></span>` : value}</div>
    <div class="stat-change">${trendId ? `<span class="stat-trend" id="${esc(trendId)}"></span>` : ''}<span class="stat-sublabel"${subId ? ` id="${esc(subId)}"` : ''}>${sub}</span></div>
  </${tag}>`;
}

export default statCard;
